import {
	Dropdown,
	companyNatureOptions,
	locationPreferenceOptions,
	natureOfBusinessOptions,
	ownershipOptions,
} from 'shared-utils';
import React, { useEffect, useState } from 'react';
import { Button, Label, Spinner, TextInput } from 'flowbite-react';
import Select, { SingleValue } from 'react-select';
import { CompanyCreateInfo, CompanyNature, CompanyOwnershipType, CompanyType, NatureOfBusinessType } from 'types';
import { useMutation, useQueryClient } from 'react-query';
import { createCompany, getMyCompaniesListQuery } from 'src/apis/company';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useToast } from 'src/context/ToastProvider';
import { get } from 'lodash';
import DynamicFieldSelection from '../position/DynamicFieldSelection';

const MIN_COMPANY_SIMILARITY = 0.5;
const MIN_SEARCH_TERM_LENGTH = 3;

const normalizeCompanyName = (name: string) => name.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

const toCompactName = (name: string) => normalizeCompanyName(name).replace(/\s/g, '');

const getBigrams = (value: string) => {
	if (value.length < 2) return [value];
	const grams: string[] = [];
	for (let i = 0; i < value.length - 1; i += 1) {
		grams.push(value.slice(i, i + 2));
	}
	return grams;
};

const getDiceSimilarity = (a: string, b: string) => {
	if (!a || !b) return 0;
	if (a === b) return 1;
	const aBigrams = getBigrams(a);
	const bBigrams = getBigrams(b);
	const bMap = new Map<string, number>();
	bBigrams.forEach(gram => bMap.set(gram, (bMap.get(gram) || 0) + 1));
	let intersection = 0;
	aBigrams.forEach(gram => {
		const count = bMap.get(gram) || 0;
		if (count > 0) {
			intersection += 1;
			bMap.set(gram, count - 1);
		}
	});
	return (2 * intersection) / (aBigrams.length + bBigrams.length);
};

const getCompanyMatchScore = (typedCompanyName: string, existingCompanyName: string) => {
	const typedCompact = toCompactName(typedCompanyName);
	const existingCompact = toCompactName(existingCompanyName);
	if (!typedCompact || !existingCompact) return 0;
	if (typedCompact === existingCompact) return 1;

	const minLengthForContainsMatch = 4;
	if (
		typedCompact.length >= minLengthForContainsMatch &&
		existingCompact.length >= minLengthForContainsMatch &&
		(typedCompact.includes(existingCompact) || existingCompact.includes(typedCompact))
	) {
		return 1;
	}
	return getDiceSimilarity(typedCompact, existingCompact);
};

const findDuplicateCompany = (typedCompanyName: string, companyNames: string[]) => {
	let topMatch = { score: 0, name: '' };
	companyNames.forEach(name => {
		const score = getCompanyMatchScore(typedCompanyName, name);
		if (score > topMatch.score) {
			topMatch = { score, name };
		}
	});
	return {
		...topMatch,
		isDuplicate: topMatch.score >= MIN_COMPANY_SIMILARITY,
	};
};

const getCompanySearchTerms = (name: string) => {
	const normalizedName = normalizeCompanyName(name);
	const compactName = toCompactName(name);
	if (!normalizedName && !compactName) return [];
	const firstWord = normalizedName.split(' ')[0] || '';
	const compactPrefix8 = compactName.slice(0, 8);
	const compactPrefix6 = compactName.slice(0, 6);

	return Array.from(new Set([firstWord, compactPrefix8, compactPrefix6].filter(term => term.length >= MIN_SEARCH_TERM_LENGTH)));
};

const mapCompaniesToOptions = (companies: { name: string }[] = []) =>
	companies.map(company => ({
		name: company.name,
		value: company.name,
	}));

const mergeUniqueCompanyOptions = (existing: { name: string; value: string }[], incoming: { name: string; value: string }[]) => {
	const uniqueByName = new Map<string, { name: string; value: string }>();
	[...existing, ...incoming].forEach(option => {
		const key = normalizeCompanyName(option.value);
		if (key) {
			uniqueByName.set(key, option);
		}
	});
	return Array.from(uniqueByName.values());
};

function AMCreateCompany() {
	const toast = useToast();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [companyDetail, setCompanyDetail] = useState<CompanyCreateInfo>({
		companyType: CompanyType.LARGE_ENTERPRISE,
		name: '',
		url: '',
	});
	const [companyOptions, setCompanyOptions] = useState<{ name: string; value: string }[]>([]);
	const [isDuplicateCompany, setIsDuplicateCompany] = useState(false);
	const [duplicateCompanyName, setDuplicateCompanyName] = useState('');

	const createCompanyFn = useMutation(['createCompanyMutation'], createCompany, {
		onSuccess(data) {
			queryClient.refetchQueries(['getMyCompaniesQuery']);
			navigate(`/my-positions/${data?.id}`);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `${errorMessage}`,
				type: 'error',
			});
		},
	});

	const handleChange = (change: Partial<CompanyCreateInfo>) => {
		setCompanyDetail({
			...companyDetail,
			...change,
		});
	};

	const handleCompanyNameChange = (name: string) => {
		const duplicateMatch = findDuplicateCompany(
			name,
			companyOptions.map(option => option.value),
		);
		setIsDuplicateCompany(duplicateMatch.isDuplicate);
		setDuplicateCompanyName(duplicateMatch.name);
		handleChange({ name });
	};

	const fetchCompanyOptionsByName = async (typedName: string) => {
		const searchTerms = getCompanySearchTerms(typedName);
		if (!searchTerms.length) {
			setCompanyOptions([]);
			return [];
		}
		const responseList = await Promise.allSettled(searchTerms.map(term => getMyCompaniesListQuery(term)));
		const fetchedOptions = responseList
			.filter((response): response is PromiseFulfilledResult<Awaited<ReturnType<typeof getMyCompaniesListQuery>>> => response.status === 'fulfilled')
			.flatMap(response => mapCompaniesToOptions(response.value?.companies || []));
		const uniqueFetchedOptions = mergeUniqueCompanyOptions([], fetchedOptions);
		setCompanyOptions(uniqueFetchedOptions);
		return uniqueFetchedOptions;
	};

	useEffect(() => {
		const normalizedName = companyDetail.name?.trim();
		if (!normalizedName) {
			setIsDuplicateCompany(false);
			setDuplicateCompanyName('');
			return;
		}
		const duplicateMatch = findDuplicateCompany(
			companyDetail.name,
			companyOptions.map(option => option.value),
		);
		setIsDuplicateCompany(duplicateMatch.isDuplicate);
		setDuplicateCompanyName(duplicateMatch.name);
	}, [companyDetail.name, companyOptions]);

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		if (isDuplicateCompany) {
			toast({
				title: duplicateCompanyName ? `Company already exists: ${duplicateCompanyName}` : 'Company already exists',
				type: 'error',
			});
			return;
		}
		const searchTerms = getCompanySearchTerms(companyDetail.name);
		if (searchTerms.length) {
			try {
				const latestOptions = await fetchCompanyOptionsByName(companyDetail.name);
				const latestMatch = findDuplicateCompany(
					companyDetail.name,
					latestOptions.map(option => option.value),
				);
				if (latestMatch.isDuplicate) {
					setIsDuplicateCompany(true);
					setDuplicateCompanyName(latestMatch.name);
					toast({
						title: `Company already exists: ${latestMatch.name}`,
						type: 'error',
					});
					return;
				}
			} catch (error) {
				// no-op: create flow continues and create API handles final validation
			}
		}
		createCompanyFn.mutate(companyDetail);
	};

	const labelStyle = `dark:text-white text-[#111928]`;
	return (
		<div className="md:py-6">
			<div className="max-w-[580px] mx-auto dark:bg-[#111E2C] bg-[#fff] p-4 md:p-6 md:rounded-lg border dark:border-0">
				<h1 className="text-xl md:text-2xl font-bold dark:text-white text-[#28293D] mb-2">Create New Company</h1>

				<form className="flex font-inter flex-col gap-4 mt-[20px]" onSubmit={handleSubmit}>
					{/* Company Name */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="name" value="Company Name" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<DynamicFieldSelection
							onChange={handleCompanyNameChange}
							onType={value => {
								void fetchCompanyOptionsByName(value);
							}}
							options={companyOptions}
							value={companyDetail.name || ''}
							placeholder="Select a Company (Type to search)"
							creatable={true}
						/>
						{isDuplicateCompany && (
							<p className="text-red-600 mt-1 text-sm">
								Company already exists{duplicateCompanyName ? `: ${duplicateCompanyName}` : ''}
							</p>
						)}
					</div>

					{/* Company URL */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="url" value="Company URL" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<TextInput
							type="text"
							onChange={e => handleChange({ url: e.target.value })}
							placeholder="https://www.levelup.masaischool.com"
						/>
					</div>

					{/* Location */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="headQuarter" value="Location" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<Select
							options={locationPreferenceOptions.map(l => ({ label: l, value: l }))}
							menuPlacement={'auto'}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							onChange={(val: SingleValue<Dropdown>) => handleChange({ headQuarter: val?.value })}
							placeholder="Bengaluru, Karnataka"
						/>
					</div>

					{/* Industry Type */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="nature" value="Industry Type" className={`${labelStyle}`} />
						</div>
						<Select
							options={companyNatureOptions}
							menuPlacement={'auto'}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							onChange={(val: SingleValue<Dropdown>) =>
								handleChange({ nature: val?.value, industry: val?.value === CompanyNature.PRODUCT ? '1' : '2' })
							}
							placeholder="Product"
						/>
					</div>

					{/* Founded In */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="url" value="Founded In" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<TextInput
							type="number"
							step={1}
							min={1800}
							max={moment().year()}
							onChange={e => handleChange({ foundedIn: Number(e.target.value) })}
							placeholder="2023"
						/>
					</div>

					{/* Head Count */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="headCount" value="Head Count" className={`${labelStyle}`} />
						</div>
						<TextInput
							type="number"
							step={1}
							min={0}
							onChange={e => handleChange({ headCount: Number(e.target.value) })}
							placeholder="100"
						/>
					</div>

					{/* Ownership */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="ownership" value="Ownership" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<Select
							options={ownershipOptions}
							menuPlacement={'auto'}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							onChange={(val: SingleValue<Dropdown>) => handleChange({ ownership: val?.value as CompanyOwnershipType })}
							placeholder="Product"
						/>
					</div>

					{/* Nature of Business */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="natureOfBusiness" value="Nature of Business" className={`${labelStyle}`} />
							<span className="text-red-600 pl-1">*</span>
						</div>
						<Select
							options={natureOfBusinessOptions}
							menuPlacement={'auto'}
							classNamePrefix="my-select-plans"
							className="single-value-select-tag"
							onChange={(val: SingleValue<Dropdown>) =>
								handleChange({ natureOfBusiness: val?.value as NatureOfBusinessType })
							}
							placeholder="Product"
						/>
					</div>

					{/* Pre-Placement Call Recording */}
					<div>
						<div className="mb-2 block">
							<Label htmlFor="prePlacementCallRecording" value="Pre-Placement Call Recording Link" className={`${labelStyle}`} />
						</div>
						<TextInput
							type="url"
							onChange={e => handleChange({ prePlacementCallRecording: e.target.value })}
							placeholder="https://..."
							value={companyDetail.prePlacementCallRecording || ''}
						/>
					</div>

					<div className="bg-white dark:bg-[#111E2C] sticky bottom-0 mb-[-16px] md:mb-[-24px] mx-[-16px] md:mx-[-24px] shadow-up px-[16px] md:px-[24px] rounded-b-md">
						<Button
							type="submit"
							color="purplePlaced"
							className="my-3 w-full"
							disabled={
								!companyDetail.name ||
								!companyDetail.url ||
								!companyDetail.headQuarter ||
								!companyDetail.foundedIn ||
								!companyDetail.ownership ||
								!companyDetail.natureOfBusiness ||
								isDuplicateCompany ||
								createCompanyFn.isLoading
							}
						>
							{createCompanyFn.isLoading ? <Spinner /> : 'Submit'}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
}

export default AMCreateCompany;
